import { Show, SignIn, UserButton } from '@clerk/react'
import { useState } from 'react'
import {
  MANUFACTURING_COPY,
  MANUFACTURING_PORTAL_COPY,
  manufacturingStatusLabel,
  type Language,
  type ManufacturingRecord,
} from '@repo/shared'
import { useGetToken } from '../lib/auth'
import {
  useManufacturingFeature,
  useManufacturingState,
  manufacturingAction,
} from '../lib/manufacturing-api'
import { WorkflowForm, WorkflowTable } from '../components/manufacturing-forms'
import { env } from '../env'
import { FactorySamplesSection, useSampleBrokerFeature } from '../components/factory-samples'

export function FactoryPortalPage() {
  const [language, setLanguage] = useState<Language>(() => {
    const code = navigator.language.slice(0, 2)
    return code === 'ko' || code === 'ja' || code === 'zh' ? code : 'en'
  })
  const enabled = useManufacturingFeature(),
    samples = useSampleBrokerFeature(),
    t = MANUFACTURING_PORTAL_COPY[language]
  // 제조 협력과 샘플 거래는 따로 켜진다. 켜진 것만 그린다 (샘플 거래는 제조 협력이 꺼져 있어도 쓴다)
  const workspaces = (
    <>
      {enabled ? <FactoryWorkspace language={language} /> : null}
      {samples?.samples ? (
        <FactorySamplesSection language={language} messages={samples.messages} />
      ) : null}
    </>
  )
  return (
    <main lang={language} className="mx-auto max-w-6xl space-y-6 p-6 text-gray-950">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">{t.title}</h1>
        <label>
          {t.language}{' '}
          <select value={language} onChange={(e) => setLanguage(e.target.value as Language)}>
            {(['ko', 'en', 'ja', 'zh'] as const).map((l) => (
              <option key={l} value={l}>
                {{ ko: '한국어', en: 'English', ja: '日本語', zh: '中文' }[l]}
              </option>
            ))}
          </select>
        </label>
      </header>
      {enabled === null || samples === null ? null : !enabled && !samples.samples ? (
        <p>{t.paused}</p>
      ) : env.E2E_TOKEN ? (
        workspaces
      ) : (
        <>
          <Show when="signed-out">
            <SignIn forceRedirectUrl="/factory" signUpForceRedirectUrl="/factory" />
          </Show>
          <Show when="signed-in">
            <UserButton />
            {workspaces}
          </Show>
        </>
      )}
    </main>
  )
}

export function FactoryWorkspace({ language }: { language: Language }) {
  const getToken = useGetToken(),
    { state, error, reload, mutate, mutating, mutationError } = useManufacturingState('factory')
  const [selected, setSelected] = useState('')
  const t = MANUFACTURING_PORTAL_COPY[language],
    c = MANUFACTURING_COPY[language],
    data = state?.workspace?.data
  const pending =
    mutating || state?.jobs.some((j) => j.status === 'queued' || j.status === 'processing') === true
  if (error)
    return (
      <div role="alert">
        {t.noAccess} {t.offline}
        <button
          type="button"
          className="block underline"
          onClick={() => void reload().catch(() => undefined)}
        >
          {t.refresh}
        </button>
      </div>
    )
  if (!state) return null
  const selectedRequest = data?.requests.find((r) => r.id === selected) ?? data?.requests[0]
  const action = (body: Record<string, unknown>) =>
    mutate(() => manufacturingAction(getToken, 'factory', body))
  const status = (value: string | undefined) => manufacturingStatusLabel(language, value ?? 'open')
  const requestContext = (record: ManufacturingRecord) => {
    const requestId =
      record.request_id ??
      data?.offers.find((offer) => offer.id === record.offer_id)?.request_id ??
      data?.orders.find((order) => order.id === record.order_id)?.request_id
    const assigned = data?.requests.find((request) => request.id === requestId)
    return (
      <div className="min-w-0 space-y-1 break-words">
        {assigned ? <p className="font-semibold">{assigned.product}</p> : null}
        <p className="break-all text-xs">{requestId ?? record.id}</p>
      </div>
    )
  }
  const until = (value: string | null | undefined) => {
    const timestamp = value ? Date.parse(value) : NaN
    return Number.isFinite(timestamp) ? new Date(timestamp).toLocaleString(language) : t.unknown
  }
  const props = {
    disabled: pending,
    submitLabel: t.send,
    queuedLabel: t.queued,
    errorLabel: t.failed,
  }
  return (
    <div className="space-y-6">
      <p>{t.scope}</p>
      <button
        type="button"
        className="underline"
        onClick={() => void reload().catch(() => undefined)}
      >
        {t.refresh}
      </button>
      {state.workspace ? (
        <p className="text-sm">
          {c.syncTime}: {new Date(state.workspace.updated_at).toLocaleString(language)}
        </p>
      ) : (
        <p>{c.waiting}</p>
      )}
      {pending ? <p role="status">{t.queued}</p> : null}
      {mutationError ? <p role="alert">{t.failed}</p> : null}
      {state.jobs[0]?.status === 'failed' ? <p role="alert">{t.failed}</p> : null}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t.requests}</h2>
        {!data?.requests.length ? (
          <p>{t.empty}</p>
        ) : (
          <select
            aria-label={t.requests}
            value={selectedRequest?.id}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full rounded border p-2"
          >
            {data.requests.map((r) => (
              <option key={r.id} value={r.id}>
                {r.product} · {r.quantity} {r.unit === '개' ? c.units : r.unit} · {status(r.status)}
              </option>
            ))}
          </select>
        )}
        {selectedRequest ? (
          <article className="space-y-3 rounded border p-4">
            <h3 className="font-semibold">{selectedRequest.product}</h3>
            <p>
              {c.requested}: {selectedRequest.quantity}{' '}
              {selectedRequest.unit === '개' ? c.units : selectedRequest.unit} ·{' '}
              {selectedRequest.unit_weight}
            </p>
            <p>
              {c.stage}: {c[selectedRequest.stage as 'prototype'] ?? selectedRequest.stage}
            </p>
            <p className="whitespace-pre-wrap">{selectedRequest.shared_brief}</p>
            {selectedRequest.can_submit ? (
              <WorkflowForm
                key={selectedRequest.id}
                {...props}
                title={t.send}
                fields={[
                  { name: 'quantity', label: c.proposed, type: 'number' },
                  { name: 'amount', label: t.amount, type: 'number', optional: true },
                  {
                    name: 'tax',
                    label: t.tax,
                    options: [
                      { value: 'included', label: t.included },
                      { value: 'excluded', label: t.excluded },
                      { value: 'unknown', label: t.unknown },
                    ],
                  },
                  { name: 'until', label: c.until, type: 'date', optional: true },
                  { name: 'conditions', label: t.conditions, type: 'textarea' },
                ]}
                onSubmit={(v) =>
                  action({
                    action: 'submit_offer',
                    request_id: selectedRequest.id,
                    proposed_quantity: v.quantity,
                    unit: selectedRequest.unit,
                    stage: selectedRequest.stage,
                    amount: v.amount || undefined,
                    currency: 'KRW',
                    tax_status: v.tax,
                    price_scope: v.amount ? 'total' : 'unknown',
                    valid_until: v.until
                      ? new Date(`${v.until}T23:59:59`).toISOString()
                      : undefined,
                    conditions: v.conditions,
                  })
                }
              />
            ) : null}
          </article>
        ) : null}
      </section>
      <WorkflowTable
        title={t.offers}
        items={data?.offers ?? []}
        columns={[
          { label: t.requests, value: requestContext },
          {
            label: c.proposed,
            value: (r) => `${r.proposed_quantity} ${r.unit === '개' ? c.units : r.unit}`,
          },
          { label: t.amount, value: (r) => r.amount ?? t.unknown },
          {
            label: t.tax,
            value: (r) =>
              r.tax_status === 'included'
                ? t.included
                : r.tax_status === 'excluded'
                  ? t.excluded
                  : t.unknown,
          },
          { label: c.until, value: (r) => until(r.valid_until) },
          { label: t.status, value: (r) => status(r.status) },
          { label: t.conditions, value: (r) => r.conditions },
        ]}
      />
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t.orders}</h2>
        {data?.orders.map((order) => (
          <article key={order.id} className="space-y-3 rounded border p-4">
            {requestContext(order)}
            <p className="break-all text-xs">{order.id}</p>
            <p>
              {order.quantity} {order.unit === '개' ? c.units : order.unit} · {order.currency}{' '}
              {order.amount} · {status(order.status)}
            </p>
            {order.can_accept ? (
              <WorkflowForm
                key={`${order.id}:accept`}
                {...props}
                title={t.accept}
                submitLabel={t.accept}
                fields={[{ name: 'evidence', label: t.evidence, type: 'textarea' }]}
                onSubmit={(v) =>
                  action({ action: 'accept_order', order_id: order.id, evidence: v.evidence })
                }
              />
            ) : null}
            {order.next_status ? (
              <WorkflowForm
                key={`${order.id}:${order.next_status}`}
                {...props}
                title={`${t.progress}: ${status(order.next_status)}`}
                submitLabel={t.progress}
                fields={[{ name: 'evidence', label: t.evidence, type: 'textarea' }]}
                onSubmit={(v) =>
                  action({
                    action: 'update_order',
                    order_id: order.id,
                    status: order.next_status,
                    evidence: v.evidence,
                  })
                }
              />
            ) : null}
          </article>
        ))}
      </section>
      <WorkflowTable
        title={t.billing}
        items={data?.billing ?? []}
        columns={[
          { label: t.requests, value: requestContext },
          { label: t.fee, value: (r) => `${r.currency} ${r.fee_amount}` },
          { label: t.received, value: (r) => r.eligible_receipts },
          { label: t.outstanding, value: (r) => r.outstanding },
          { label: t.credit, value: (r) => r.credit_due },
        ]}
      />
    </div>
  )
}
